import { deflate, Inflate } from "pako";
import moment from "moment";

// --- decompression limits ---------------------------------------------------
// The token comes straight from a URL a stranger can craft, so inflating it
// unbounded is a denial-of-service: deflate compresses repetitive input at
// roughly 1000:1, so a ~5 KB token expands to ~5 MB and a ~64 KB one to
// hundreds of MB, freezing the tab.
//
// pako 3.0.0 has no `maxOutputLength` option, so the cap is enforced with the
// streaming Inflate API, aborting as soon as the accumulated output exceeds it.
// A real share payload is a few hundred bytes; these ceilings are far above any
// legitimate link.
const MAX_TOKEN_BYTES = 8 * 1024;
const MAX_INFLATED_BYTES = 64 * 1024;

const inflateWithLimit = (bytes) => {
    if (bytes.length > MAX_TOKEN_BYTES) {
        throw new Error("Share token exceeds the maximum size");
    }

    const inflater = new Inflate();
    const chunks = [];
    let total = 0;

    inflater.onData = (chunk) => {
        total += chunk.length;
        if (total > MAX_INFLATED_BYTES) {
            throw new Error("Share token expands beyond the maximum size");
        }
        chunks.push(chunk);
    };

    inflater.push(bytes, true);

    if (inflater.err) {
        throw new Error(inflater.msg || "Failed to inflate share token");
    }

    const out = new Uint8Array(total);
    let offset = 0;
    chunks.forEach((chunk) => {
        out.set(chunk, offset);
        offset += chunk.length;
    });
    return out;
};

// NOTE (open finding I-2): the payload carries no schema version. If
// SHARE_KEY_ALIASES below ever changes meaning, links already circulating in
// chat apps will decode into the WRONG fields with no way to detect it.
// Versioning was implemented and then reverted to keep the token byte-identical
// to the current format. It cannot be retrofitted to links already issued, so
// the longer this waits the more links are affected. See QA_FINDINGS.md.

// Web-based package share links.
//
// Instead of generating a Branch short link we extract a small, FLAT object from
// the shared package (selectedHotelToShare) — only the fields needed to run the
// API search and to display the package card — serialize it to JSON, compress it
// with pako (deflate) and base64url-encode the result so it can travel safely
// inside a query parameter. Keeping the data flat and minimal keeps the link
// short. The receiving page reverses the process to get the flat object back.

// --- base64url helpers (URL-safe, no padding) ---
const toBase64Url = (bytes) => {
    let binary = "";
    for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary)
        .replace(/\+/g, "-")
        .replace(/\//g, "_")
        .replace(/=+$/, "");
};

const fromBase64Url = (str) => {
    let base64 = str.replace(/-/g, "+").replace(/_/g, "/");
    while (base64.length % 4) {
        base64 += "=";
    }
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
};

/**
 * Compress a string into a URL-safe token.
 * @param {string} value e.g. a JSON string
 * @returns {string} compressed, base64url-encoded token
 */
export const encodeShareParams = (value) => {
    // level 9 = maximum compression, to keep the resulting link as short as possible.
    const compressed = deflate(value, { level: 9 });
    return toBase64Url(compressed);
};

/**
 * Reverse of encodeShareParams: restore the original string.
 * @param {string} token compressed, base64url-encoded token
 * @returns {string} original string
 */
export const decodeShareParams = (token) => {
    const bytes = fromBase64Url(token);
    const decoded = new TextDecoder().decode(inflateWithLimit(bytes));

    try {
        return JSON.stringify(expandShareData(JSON.parse(decoded)));
    } catch {
        return decoded;
    }
};

/**
 * Reverse of encodeShareParams, returning the expanded OBJECT.
 * decodeShareParams keeps returning a string for backwards compatibility, which
 * previously forced callers to stringify and re-parse.
 */
export const decodeShareObject = (token) => {
    const decoded = new TextDecoder().decode(inflateWithLimit(fromBase64Url(token)));
    return expandShareData(JSON.parse(decoded));
};

// Central mapping from full share-data keys to short payload keys.
const SHARE_KEY_ALIASES = {
    hotelId: "hI",
    name: "n",
    image: "i",
    starRating: "sR",
    guestRatingReviewText: "gR",
    address: "a",
    perPersonPrice: "pP",
    currency: "c",
    departure: "d",
    destination: "ds",
    departureDate: "dD",
    returnDate: "rD",
    airlineCode: "aC",
    rooms: "rm",
    adults: "ad",
    children: "ch",
    infants: "if",
    flightNumbersString: "fN",
};

const SHORT_TO_SHARE_KEY = Object.fromEntries(
    Object.entries(SHARE_KEY_ALIASES).map(([original, alias]) => [alias, original])
);

// Any key without an alias passes through unchanged, so a future field literally
// named "n" or "a" would collide with the alias for name/address. Guarded here
// rather than left to chance.
const SHORT_KEYS = new Set(Object.values(SHARE_KEY_ALIASES));

const compactShareData = (data) =>
    Object.entries(data).reduce((result, [key, value]) => {
        const alias = SHARE_KEY_ALIASES[key];
        if (!alias && SHORT_KEYS.has(key)) {
            throw new Error(
                `Share payload key "${key}" collides with a short alias; add it to SHARE_KEY_ALIASES`
            );
        }
        result[alias || key] = value;
        return result;
    }, {});

const expandShareData = (data) =>
    Object.entries(data).reduce((result, [key, value]) => {
        result[SHORT_TO_SHARE_KEY[key] || key] = value;
        return result;
    }, {});

// Query-string key under which the compressed token travels in the share link.
export const SHARE_PARAM_KEY = "data";

// Parse the room / passenger counts the package carries inside the webDetails
// href (e.g. ...&NumRoom=1&NumAdult=R1:2&NumChild=R1:0). Values look like
// "R1:2|R2:1"; we sum the number after each ":".
const sumPaxParam = (value) =>
    (value || "")
        .split("|")
        .reduce((total, part) => total + (Number(part.split(":")[1]) || 0), 0);

const parsePaxFromHref = (href) => {
    // The source href carries no infant count, so infants stays 0.
    const result = { rooms: 1, adults: 0, children: 0, infants: 0 };
    if (!href) {
        return result;
    }
    try {
        const params = new URL(href).searchParams;
        result.rooms = Number(params.get("NumRoom")) || 1;
        result.adults = sumPaxParam(params.get("NumAdult"));
        result.children = sumPaxParam(params.get("NumChild"));
    } catch {
        // keep defaults
    }
    return result;
};

// Date part ("2026-08-28") from a flight leg's ISO datetime.
const toDate = (dateTime) => (dateTime ? String(dateTime).split("T")[0] : undefined);

const toShareDateMoment = (value) => {
    if (typeof value !== "string" || !value) {
        return null;
    }
    return moment.parseZone(value, moment.ISO_8601, true);
};

/**
 * Extract a small, FLAT object from the shared package containing only what is
 * needed to (a) run the API search and (b) display the package card. No nesting,
 * so the compressed link stays as short as possible.
 *
 * @param {object} selectedHotelToShare the package object from redux (DetailsTopBar)
 * @returns {object|null} the flat share data
 */
export const buildShareData = (selectedHotelToShare, flight) => {
    if (!selectedHotelToShare) {
        return null;
    }

    const hotel = selectedHotelToShare;
    const flightOffer = flight;

    // departure ticket data
    const departureSegmentId = flightOffer?.segmentIds?.[0];
    const departureFlightSegment = flightOffer?.segments?.find(
        (segment) => segment.segmentId === departureSegmentId
    );
    const outboundLeg = departureFlightSegment?.legs?.[0];

    // arrival (return) ticket data
    const arrivalSegmentId =
        flightOffer?.segmentIds?.[flightOffer?.segmentIds?.length - 1];
    const arrivalFlightSegment = flightOffer?.segments?.find(
        (segment) => segment.segmentId === arrivalSegmentId
    );
    const returnLeg = arrivalFlightSegment?.legs?.[0];

     const flightNumbersString = flightOffer?.segmentIds
        ?.map((segmentId) =>
            flightOffer?.segments?.find((segment) => segment.segmentId === segmentId)
        )
        ?.flatMap((segment) => segment?.legs ?? [])
        ?.map((leg) => leg?.flightNumber)
        ?.filter(Boolean)
        ?.join("|"); // returns string like "AA123|AA456|AA789"

    const pax = parsePaxFromHref(hotel?.roomTypes?.[0]?.links?.webDetails?.href);

    return compactShareData({
        // --- hotel / display ---
        hotelId: hotel?.id,
        name: hotel?.name,
        image: hotel?.thumbnailUrl || hotel?.media?.[0]?.url,
        starRating: hotel?.starRating,
        // combined rating + review count, e.g. "8.8 (150)".
        // NOTE (open finding I-5): an unrated hotel encodes as "0 (0)", which
        // reads as a score of zero rather than "not rated". Left as-is so the
        // token stays byte-identical to the current format.
        guestRatingReviewText: `${hotel?.guestRating || 0} (${hotel?.guestReviewCount || 0})`,
        // combined city + country, e.g. "Barcelona, Spain"
        address: [hotel?.location?.address?.city, hotel?.location?.address?.country]
            .filter(Boolean)
            .join(", "),
        // --- price ---
        // value only; the currency lives in its own property
        perPersonPrice: hotel?.perPersonPrice?.totalPrice,
        currency: hotel?.price?.currency || hotel?.perPersonPrice?.currency,
        // --- flight / search inputs ---
        departure: outboundLeg?.departureAirport?.code,
        destination: returnLeg?.departureAirport?.code,
        departureDate: toDate(outboundLeg?.departureDateTime),
        returnDate: toDate(returnLeg?.departureDateTime),
        airlineCode: outboundLeg?.marketingAirlineCode,
        // --- passengers / rooms ---
        rooms: pax.rooms,
        adults: pax.adults,
        children: pax.children,
        infants: pax.infants,
        flightNumbersString,
    });
};

/**
 * Build a web-based package share link from the shared package object.
 * Embeds the flat share data as a compressed token in the format:
 *   https://{current-host}/link?data={token}
 * The host is captured dynamically from window.location (never hardcoded).
 *
 * @param {object} selectedHotelToShare the package object from redux (DetailsTopBar)
 * @returns {string} the share link, or "" when there is no package to share
 */
export const buildShareLink = (selectedHotelToShare, flight) => {
    const data = buildShareData(selectedHotelToShare, flight);
    // Without this, buildShareData's null return would be encoded as the string
    // "null" and handed back as a perfectly plausible-looking link that decodes
    // to nothing for whoever opens it.
    if (!data) {
        return "";
    }
    const token = encodeShareParams(JSON.stringify(data));
    return `${window.location.origin}/link?${SHARE_PARAM_KEY}=${token}`;
};

/**
 * Decode a share-link token back into the flat share-data object.
 * @param {string} token compressed, base64url-encoded token
 * @returns {object|null} the flat share data, or null if it cannot be decoded
 */
export const decodeShareData = (token) => {
    if (!token) {
        return null;
    }
    try {
        return decodeShareObject(token);
    } catch (e) {
        console.error("Failed to decode shared package link:", e);
        return null;
    }
};

/**
 * Build the listing filter chips (date range / travelers-rooms / nights) for the
 * Web Link card from the FLAT share data. Mirrors the chips produced by
 * getListingItemFilters (src/helpers/common.js) for the nested Branch offer.
 *
 * @param {object} data the flat share data
 * @param {(key: string) => string} T translation function
 * @returns {Array<{id: number, title: string}>} filter chips
 */
export const getWebLinkFilters = (data, T) => {
    if (!data) {
        return [];
    }
    const checkInDate = data.departureDate;
    const checkOutDate = data.returnDate;
    const checkInMoment = toShareDateMoment(checkInDate);
    const checkOutMoment = toShareDateMoment(checkOutDate);
    const hasValidDateRange =
        checkInMoment?.isValid() &&
        checkOutMoment?.isValid() &&
        !checkOutMoment.isBefore(checkInMoment, "day");

    const total =
        (Number(data.adults) || 0) + (Number(data.children) || 0) + (Number(data.infants) || 0);
    const rooms = Number(data.rooms) || 1;
    const chips = [];

    if (hasValidDateRange) {
        const nights = checkOutMoment.diff(checkInMoment, "days");
        chips.push(
            {
                id: 1,
                title: `${checkInMoment.format("MMM DD")}-${checkOutMoment.format("DD")}`,
            },
            {
                id: 3,
                title: `${nights} ${nights > 1 ? T("Common:Nights") : T("Common:Night")}`,
            }
        );
    }

    chips.push({
        id: 2,
        title: `${total} ${T("Home:Home_List_View_Pers")} / ${rooms} ${T("Home:Home_List_View_Rm")}`,
    });

    return chips.sort((a, b) => a.id - b.id);
};
